import { useState, useEffect, useCallback, useRef } from 'react';
import { planRoute, fetchProfiles, precomputeRoutes } from '../utils/api';
import type { RouteResponse, ProfileInfo, WeightOverrides, PrecomputeResponse, ComputedRoute, RoutingWeights } from '../types/api';

// Slider -> bundle matching: find the precomputed route whose weight tuple is
// closest (Euclidean in weight space) to the current slider values. Missing
// slider values use the "normal" base profile defaults so partial slider
// states still match something sensible.
const BASE_NORMAL: RoutingWeights = { distance: 3.0, stress: 0.5, climb: 1.0, surface: 0.5 };

function nearestBundleEntry(
  bundle: ComputedRoute[],
  sliders: WeightOverrides,
): ComputedRoute | null {
  if (bundle.length === 0) return null;
  const target: RoutingWeights = {
    distance: sliders.distance ?? BASE_NORMAL.distance,
    stress: sliders.stress ?? BASE_NORMAL.stress,
    climb: sliders.climb ?? BASE_NORMAL.climb,
    surface: sliders.surface ?? BASE_NORMAL.surface,
  };
  let best: ComputedRoute | null = null;
  let bestDist = Infinity;
  for (const r of bundle) {
    if (!r.weights) continue;
    const dd = r.weights.distance - target.distance;
    const ds = r.weights.stress - target.stress;
    const dc = r.weights.climb - target.climb;
    const du = r.weights.surface - target.surface;
    const dist = dd * dd + ds * ds + dc * dc + du * du;
    if (dist < bestDist) {
      bestDist = dist;
      best = r;
    }
  }
  return best;
}

export function useRoutePlanner() {
  const [routes, setRoutes] = useState<RouteResponse | null>(null);
  const [profiles, setProfiles] = useState<ProfileInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [weightOverrides, setWeightOverrides] = useState<WeightOverrides>({});
  const [selectedRoute, setSelectedRoute] = useState<number>(0);
  const [precomputing, setPrecomputing] = useState(false);

  // Precomputed bundle for the current OD pair. When present, slider scrubbing
  // picks the nearest bundle entry client-side for instant feedback.
  const precomputeBundleRef = useRef<ComputedRoute[] | null>(null);
  const [bundleReady, setBundleReady] = useState(false);

  useEffect(() => {
    fetchProfiles()
      .then(setProfiles)
      .catch(() => {});
  }, []);

  const planRoutes = useCallback(
    async (origin: { lat: number; lon: number }, destination: { lat: number; lon: number }, explore: boolean = false) => {
      setLoading(true);
      setError(null);
      setRoutes(null);
      precomputeBundleRef.current = null;
      setBundleReady(false);

      try {
        // 1. Initial routes via the standard endpoint (fast, ~4 profiles).
        const result = await planRoute({
          origin: { lat: origin.lat, lon: origin.lon },
          destination: { lat: destination.lat, lon: destination.lon },
          weight_overrides: hasOverrides(weightOverrides) ? weightOverrides : undefined,
        }, explore);
        setRoutes(result);
        setSelectedRoute(0);

        // 2. Kick off precompute in the background (cached server-side per OD pair).
        //    On success the bundle replaces `routes` so slider scrubbing is instant.
        setPrecomputing(true);
        precomputeRoutes({
          origin: { lat: origin.lat, lon: origin.lon },
          destination: { lat: destination.lat, lon: destination.lon },
        }, explore)
          .then((pre: PrecomputeResponse) => {
            if (pre.routes.length > 0) {
              precomputeBundleRef.current = pre.routes;
              setBundleReady(true);
              // Immediately apply current slider selection to the bundle.
              const nearest = nearestBundleEntry(pre.routes, weightOverrides);
              if (nearest) {
                setRoutes({ routes: pre.routes });
                const idx = pre.routes.findIndex((r) => r === nearest);
                if (idx >= 0) setSelectedRoute(idx);
              }
            }
          })
          .catch(() => { /* precompute is best-effort; initial routes still work */ })
          .finally(() => setPrecomputing(false));
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Planning failed');
      } finally {
        setLoading(false);
      }
    },
    [weightOverrides]
  );

  // When sliders change and a bundle is ready, pick the nearest bundle entry
  // with no network round-trip. Debounced so dragging is smooth.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const applySlidersToBundle = useCallback(() => {
    const bundle = precomputeBundleRef.current;
    if (!bundle || bundle.length === 0) return;
    const nearest = nearestBundleEntry(bundle, weightOverrides);
    if (nearest) {
      const idx = bundle.findIndex((r) => r === nearest);
      if (idx >= 0) {
        setRoutes({ routes: bundle });
        setSelectedRoute(idx);
      }
    }
  }, [weightOverrides]);

  useEffect(() => {
    if (!bundleReady) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(applySlidersToBundle, 120);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [weightOverrides, bundleReady, applySlidersToBundle]);

  const updateWeight = useCallback((key: keyof WeightOverrides, value: number | undefined) => {
    setWeightOverrides((prev) => ({ ...prev, [key]: value }));
  }, []);

  return {
    routes,
    profiles,
    loading,
    precomputing,
    bundleReady,
    error,
    weightOverrides,
    updateWeight,
    planRoutes,
    selectedRoute,
    setSelectedRoute,
  };
}

function hasOverrides(w: WeightOverrides): boolean {
  return Object.values(w).some((v) => v !== undefined && v !== null);
}